import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  AcceptOffersIcon,
  AlertService,
  Button,
  DataTableNaked,
  RowActionInstanceUnaward,
  Search,
  Timing,
  Theme as theme,
  UnawardIcon,
  usePolling,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import deleteCachedFieldsOnListedInventoriesQuery from '../../apollo/cache-helpers/delete-cached-fields-on-offer-review-polling-query';
import deleteCachedFieldsOnUserQuery from '../../apollo/cache-helpers/delete-cached-fields-on-user-query';
import deleteCachedObject from '../../apollo/cache-helpers/delete-cached-object';
import UpdatedAwardedOffers from '../../components/banners/updated-awarded-offers';
import { getColumnsFromDataTableProfile } from '../../components/data-table';
import PostAwardUpdatesModal from '../../components/modals/post-award-updates-modal';
import {
  acceptOffer as AcceptOfferMutation,
  UnawardOfferListingsByBuyerSiteId as UnawardOfferListingsByBuyerSiteIdMutation,
  UnawardOfferListingsByTruckLane as UnawardOfferListingsByTruckLaneMutation,
  UnawardOfferListingsBySellerLocationId as unawardOfferListingsBySellerLocationIdMutation,
} from '../../graphql/mutations';
import { AwardSummaryQuery, OfferListingsByBuyerQuery, OfferReviewDetailsPollingQuery, ProgressBarsQuery } from '../../graphql/queries';
import routePaths from '../../route-paths';
import { TitleService } from '../../services';
import { Breadcrumbs, UserFilters } from '../../store';
import { OfferComparisonStrings } from '../../string-resources';
import { weightWithUnit } from '../../utilities/unit-helper';

export const styles = {
  buyer_detail_wrap: {
    marginBottom: -40,
  },
  buyer_detail_status: {
    position: 'relative',
    top: '-40px',
    height: 0,
    left: '275px',
  },
  buyer_detail_container: {
    minHeight: 'calc(100vh - 106px)',
  },
  buyer_detail_actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: [0, 0, 24],
    '&>button': {
      marginLeft: 8,
    },
    position: 'relative',
  },
  table__container: {
    border: '1px #D1D6DA solid',
    borderRadius: 2,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& td': {
      padding: 5,
    },
  },
  table__totalrow: {
    fontWeight: 600,
    borderTop: '1px solid',
    borderTopColor: theme.tableHeaderTextColor,
  },
  table__headerrow: {
    color: theme.tableHeaderTextColor,
    fontWeight: 600,
  },
  table__numeric: {
    textAlign: 'right',
  },
  table__link: {
    backgroundColor: `${theme.errorColor} !important`,
    color: '#fff !important',
    display: 'inline-block !important',
  },
  modal__title: {
    fontSize: '1.5rem',
    padding: 0,
    marginTop: 0,
    color: '#292e34',
    fontWeight: 'normal',
  },
  offerReviewSearch: {
    width: 350,
    marginRight: 'auto',
  },
  buttonIcon: {
    '& svg': {
      width: '24px !important',
      height: '24px !important',
    },
  },
  bannerContainer: {
    margin: '24px 0px',
  },
};

const useStyles = createUseStyles(styles);

const offerFilterCompState = {
  sites: [],
  status: [
    { text: 'Offers Made', value: 'Offers Made' },
    { text: 'Offers Awarded', value: 'Offers Awarded' },
    { text: 'Partially Awarded', value: 'Partially Awarded' },
  ],
  showTied: false,
  belowReservePrice: false,
  suggestedAward: false,
  postAwardUpdates: true,
  categories: [],
  businessUnits: [],
  truckTypes: [],
  negotiationStatus: [],
  logistics: [],
  inventoryTypes: [],
  codeDate: { start: undefined, end: undefined },
  trucklaneStatus: [],
  distributionLists: [],
  importBatches: [],
};

const OfferLogisticsReview = ({ match, user, history }) => {
  const classes = useStyles();
  const {
    params: { buyerSiteId },
  } = match;
  const buyerSiteName = decodeURIComponent(match.params.buyerSiteName);

  // State
  const [searchText, setSearchText] = useState('');
  const [displayAwardedOffersModal, setDisplayAwardedOffersModal] = useState(false);

  const pollResponse = usePolling({
    idleInterval: 10000,
    activeInterval: 2000,
    isInProgress: (data) => data.exportStatus === 'IN_PROGRESS',
    poll: true,
    query: OfferReviewDetailsPollingQuery,
    responseKey: 'offerReviewDetailsPollingQuery',
    parameters: { buyerSiteId },
  });

  const blockAction = pollResponse ? pollResponse.exportStatus === 'IN_PROGRESS' : true;

  const [actionOnRows, updateActionOnRows] = useReducer((state, action) => {
    switch (action.type) {
      case 'addRow':
        return [...state, action.rowId];
      case 'removeRow':
        return state.filter((row) => row !== action.rowId);
      default:
        return null;
    }
  }, []);

  const postAwardModalRef = useRef(false);

  useEffect(() => {
    if (postAwardModalRef.current === true) {
      postAwardModalRef.current = false;
      UserFilters.updateFilters('offerComparison', offerFilterCompState);
      history.push(`${routePaths.offerComparison}`);
    }
  }, [displayAwardedOffersModal]);

  // Apollo
  /// Cache Access
  const { cache: apolloCache } = useApolloClient();

  /// Queries
  const {
    data: offerListingsByBuyerData,
    loading: offerListingsByBuyerLoading,
    refetch: offerListingsByBuyerRefetch,
  } = useQuery(OfferListingsByBuyerQuery, {
    variables: { buyerSiteIds: [buyerSiteId] },
  });

  /// Mutations
  const [acceptOffers, { loading: acceptingAll }] = useMutation(AcceptOfferMutation);
  const [unawardOfferListingsByBuyerSiteId, { loading: unawardingAll }] = useMutation(UnawardOfferListingsByBuyerSiteIdMutation);
  const [unawardOfferListingsBySellerLocationId] = useMutation(unawardOfferListingsBySellerLocationIdMutation);
  const [unawardOfferListingsByTruckLane] = useMutation(UnawardOfferListingsByTruckLaneMutation);

  useEffect(() => {
    // On initial load of this page, if there is already data and we are not loading more, we want to refetch as other pages
    //  in the app are calling 'OfferListingsByBuyerQuery' but then the cache is not updated when changes happen to awarded offers
    if (offerListingsByBuyerData && !offerListingsByBuyerLoading) offerListingsByBuyerRefetch();

    TitleService.setTitles('Offer Review');
    Breadcrumbs.set([
      {
        url: routePaths.review,
        title: 'Offer Review',
      },
      {
        url: match.url,
        title: `${buyerSiteName}${(offerListingsByBuyerData?.offerListingsByBuyerQuery[0]?.isVendorOfRecord && ' (Spoiler Alert Buyer)') || ''}`,
        badge: { text: 'NOT ACCEPTED', color: 'grey' },
      },
    ]);
  }, [buyerSiteName, match.url, offerListingsByBuyerData, offerListingsByBuyerLoading, offerListingsByBuyerRefetch]);

  const successMessage = (message) => {
    AlertService.alert({
      type: 'success',
      autoDismiss: true,
      message: <span>{message}</span>,
    });
  };

  const infoMessage = (message) => {
    AlertService.alert({
      type: 'info',
      autoDismiss: true,
      message: <span>{message}</span>,
    });
  };

  const errorMessage = (message) => {
    AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{message}</span> });
  };

  // The Truck Count column lives in the data table profile unconditionally, but only means anything when the
  // site has a truck maximum configured. That setting is editable at any time, so visibility is decided here
  // rather than persisted on the column. Mirrors the offer card's trucklane details panel.
  const truckMaximumsSet = user?.site?.truckMaximums?.mode !== 'none' && (user?.site?.truckMaximums?.weight || user?.site?.truckMaximums?.pallets);

  const columnOverrides = {
    logisticsTerm: {
      groupHeader: (key) => key,
    },
    weight: {
      groupSummary: (rows) =>
        weightWithUnit(
          rows.reduce((sum, row) => sum + row.weight, 0),
          user,
        ),
    },
    cubeAdjustedWeight: {
      groupSummary: (rows) =>
        weightWithUnit(
          rows.reduce((sum, row) => sum + row.cubeAdjustedWeight, 0),
          user,
        ),
    },
    cases: {
      groupSummary: (rows) => rows.reduce((sum, row) => sum + row.cases, 0),
    },
    pallets: {
      groupSummary: (rows) => rows.reduce((sum, row) => sum + row.pallets, 0).toFixed(2),
    },
    truckCount: {
      // Each row's truckCount already arrives rounded up to the next tenth from the aggregation, so this sums
      // per-lane counts that are individually rounded — matching calculateMetricTruckCount and the cost
      // multiplier, which likewise round per lane, since trucks can't be shared between lanes. toFixed only
      // cleans up float noise (0.1 + 0.2). truckCount is null for lanes whose truck type has no maximum, so
      // those are skipped and a group with none reads as a dash, the same as its cells.
      groupSummary: (rows) => {
        const counts = rows.map((row) => row.truckCount).filter((count) => typeof count === 'number');
        return counts.length ? counts.reduce((sum, count) => sum + count, 0).toFixed(1) : '—';
      },
    },
    offerPrice: {
      groupSummary: (rows) => accounting.formatMoney(rows.reduce((sum, row) => sum + row.offerPrice, 0)),
    },
    items: {
      groupSummary: (rows) => rows.reduce((sum, row) => sum + row.items, 0),
    },
    truckType: {
      style: { textTransform: 'capitalize' },
    },
    totalRevenue: {
      groupSummary: (rows) =>
        `${accounting.formatNumber(
          (rows.reduce((sum, row) => sum + row.totalRevenue, 0) / rows.reduce((sum, row) => sum + row.costOfInventoryAwarded, 0)) * 100,
          2,
        )}%`,
    },
    costRecoveryRate: {
      groupSummary: (rows) => {
        const totalCost = rows.reduce((sum, row) => sum + row.costOfInventoryAwarded, 0);
        const acceptedOffer = rows.reduce((sum, row) => sum + row.totalRevenue, 0);
        return `${((acceptedOffer / totalCost) * 100).toFixed(2)}%`;
      },
    },
    unitPriceRecoveryRate: {
      groupSummary: (rows) => {
        const totalUnitPriceRecoveryRate = rows.reduce((sum, row) => sum + row.unitPriceRecoveryRate, 0);
        return `${(totalUnitPriceRecoveryRate / rows.length).toFixed(2)}%`;
      },
    },
    priceOfInventoryAwarded: {
      groupSummary: (rows) => accounting.formatMoney(rows.reduce((sum, row) => sum + row.priceOfInventoryAwarded, 0)),
    },
    writeOffDiscountPercent: {
      groupSummary: (rows) => {
        const offerPrice = rows.reduce((sum, row) => sum + row.offerPrice, 0);
        const writeOffPriceOfInventoryAwarded = rows.reduce((sum, row) => sum + row.writeOffPriceOfInventoryAwarded, 0);
        const result =
          offerPrice && writeOffPriceOfInventoryAwarded ? ((1 - offerPrice / writeOffPriceOfInventoryAwarded) * 100).toFixed(2) : undefined;
        return Number.isNaN(result) ? undefined : `${result}%`;
      },
    },
    trucklaneCost: {
      groupSummary: (rows) => {
        const total = rows[0].logisticsTerm === 'Delivery' ? rows.reduce((sum, row) => sum + row.trucklaneCost, 0) : '-';
        return total > -1 ? `(${accounting.formatMoney(total)})` : total;
      },
    },
    netRevenue: {
      groupSummary: (rows) => {
        const totalRev = rows.reduce((sum, row) => sum + (row.totalRevenue - row.trucklaneCost), 0);
        return totalRev > -1 ? accounting.formatMoney(totalRev) : `(${accounting.formatMoney(-1 * totalRev)})`;
      },
    },
  };

  const handleSearch = Timing.debounce((text) => setSearchText(text), 300);

  const deleteCachedOfferListingSummaryTypesForBuyer = (cache) => {
    offerListingsByBuyerData?.offerListingsByBuyerQuery.forEach((offerListingSummaryType) => {
      deleteCachedObject(
        cache,
        'OfferListingSummaryType',
        `${buyerSiteId}_${offerListingSummaryType.logisticsTerm}_${offerListingSummaryType.truckType}_${offerListingSummaryType.transactionLocationId}_${offerListingSummaryType.sellerSiteId}`,
      );
    });
  };

  useEffect(() => {
    let badge;
    switch (pollResponse?.exportStatus) {
      case 'NOT_IN_PROGRESS':
        badge = { text: 'NOT ACCEPTED', color: 'grey' };
        break;
      case 'IN_PROGRESS':
        badge = { text: 'ACCEPTANCE IN PROGRESS', color: 'yellow' };
        break;
      case 'COMPLETE':
        badge = { text: 'COMPLETED', color: 'green' };
        break;
      default:
    }
    Breadcrumbs.update(1, {
      url: match.url,
      title: buyerSiteName,
      badge,
    });
  }, [pollResponse?.exportStatus, acceptingAll]);

  // Click Handlers
  const handleRowClick = (row) => {
    const trucklaneId = `${row.truckType}:${row.logisticsTerm}:${row.sellerSiteId}:${row.buyerDestinationId}:${user.site.transactionCycleId}`;
    history.push(`${routePaths.offerComparison}/trucklane/${trucklaneId}`);
  };

  const displayPostUpdatesAward = useMemo(() => {
    let displayPostUpdatesAwardFlag = false;
    if (pollResponse && pollResponse?.displayPostAwardUpdates > 0) {
      displayPostUpdatesAwardFlag = true;
    }
    return displayPostUpdatesAwardFlag;
  }, [pollResponse?.displayPostAwardUpdates]);

  /// Page Action Button 'Accept All'
  const handleAcceptAllClick = useCallback(() => {
    acceptOffers({
      variables: {
        buyerSiteIds: [buyerSiteId],
        acceptAll: false,
      },
      update: (cache) => {
        deleteCachedFieldsOnListedInventoriesQuery(cache);
      },
      refetchQueries: [{ query: ProgressBarsQuery }, { query: AwardSummaryQuery }],
    })
      .then(() => {
        infoMessage(`Acceptance for ${buyerSiteName} has been initiated.`);
        deleteCachedOfferListingSummaryTypesForBuyer(apolloCache);
        history.push(routePaths.review);
      })
      .catch(() => {
        errorMessage(OfferComparisonStrings.awardingError);
      });
  }, [acceptOffers, apolloCache, buyerSiteId, buyerSiteName, history]);

  const displayAwardedOfferModalHandler = () => {
    setDisplayAwardedOffersModal(true);
  };

  const navigateToOfferCompScreen = () => {
    postAwardModalRef.current = true;
    setDisplayAwardedOffersModal(false);
  };

  const closeAwardedOffersModal = () => {
    setDisplayAwardedOffersModal(false);
  };

  /// Page Action Button 'Unaward All'
  const handleUnawardAllClick = async () => {
    const response = await unawardOfferListingsByBuyerSiteId({
      variables: { siteId: buyerSiteId }, // Unaward by buyer parent site id
      update: (cache) => {
        deleteCachedOfferListingSummaryTypesForBuyer(cache);
        deleteCachedFieldsOnUserQuery(cache, ['awardedOfferListings', 'awardedInventoryFilterParameters', 'getAwardSummary']);
      },
    });
    if (response?.data?.unawardOfferListingsByBuyerSiteId?.errors?.length > 0) return errorMessage(OfferComparisonStrings.unawardingError);

    successMessage(`All offers from ${buyerSiteName} have been unawarded.`);
    history.push(`${routePaths.review}`);
  };

  const handleUnawardBySellerLocation = async (rows, e) => {
    e.stopPropagation();
    const { sellerSiteId: sellerLocationId, sellerSiteName } = rows[0];
    updateActionOnRows({ type: 'addRow', rowId: sellerSiteName });
    const response = await unawardOfferListingsBySellerLocationId({
      variables: { buyerSiteId, sellerLocationId },
      refetchQueries: [
        { query: ProgressBarsQuery },
        { query: AwardSummaryQuery },
        { query: OfferListingsByBuyerQuery, variables: { buyerSiteIds: [buyerSiteId] } },
      ],
    });
    updateActionOnRows({ type: 'removeRow', rowId: sellerSiteName });

    if (response?.data?.unawardOfferListingsBySellerLocationId?.errors?.length > 0) {
      return errorMessage(OfferComparisonStrings.unawardingError);
    }

    const sellerSiteNameSet = new Set();
    offerListingsByBuyerData?.offerListingsByBuyerQuery.forEach((offerListing) => {
      sellerSiteNameSet.add(offerListing.sellerSiteName);
    });
    const message = sellerSiteNameSet.size === 1 ? `All offers from ${buyerSiteName} have been unawarded.` : 'The offers have been unawarded.';
    successMessage(message);
    if (sellerSiteNameSet.size === 1) {
      history.push(`${routePaths.review}`);
    }
  };

  const handleUnawardByTruckLaneClick = async (row, e) => {
    e.stopPropagation();
    const { _id: rowId, sellerSiteId: sellerLocationId, transactionLocationId, truckType, logisticsTerm } = row;
    updateActionOnRows({ type: 'addRow', rowId: rowId });
    const response = await unawardOfferListingsByTruckLane({
      variables: {
        buyerSiteId,
        sellerLocationId,
        transactionLocationId,
        truckType,
        logisticsTerm,
      },
      refetchQueries: [
        { query: ProgressBarsQuery },
        { query: AwardSummaryQuery },
        { query: OfferListingsByBuyerQuery, variables: { buyerSiteIds: [buyerSiteId] } },
      ],
    });
    updateActionOnRows({ type: 'removeRow', rowId });

    if (response?.data?.unawardOfferListingsByTruckLane?.errors?.length > 0) {
      return errorMessage(OfferComparisonStrings.unawardingError);
    }

    successMessage('All offers from the trucklane have been unawarded.');
    if (offerListingsByBuyerData?.offerListingsByBuyerQuery.length === 1) {
      history.push(`${routePaths.review}`);
    }
  };

  const rowActions = [
    <RowActionInstanceUnaward
      key={1}
      onClick={(row) => handleUnawardByTruckLaneClick.bind(this, row)}
      disabledRows={actionOnRows}
      loadingRows={actionOnRows}
    />,
  ];

  const headerRowActions = [
    <RowActionInstanceUnaward
      key={1}
      headerRowOnClick={(row) => handleUnawardBySellerLocation.bind(this, row)}
      disabledRows={actionOnRows}
      loadingRows={actionOnRows}
    />,
  ];

  const modalInnerText = `${pollResponse?.displayPostAwardUpdates || 0} of this customer's have offers that have changed since they were awarded. Offers cannot be accepted for
  this customer until the updated offers are approved or rejected.`;

  return (
    <div className={classes.buyer_detail_wrap}>
      <div className={classes.buyer_detail_container}>
        <div className={classes.buyer_detail_actions}>
          <Search onChange={handleSearch} className={classes.offerReviewSearch} />
          <Button
            onClick={handleUnawardAllClick}
            disabled={blockAction}
            loading={unawardingAll}
            loadingText="Unawarding"
            icon={UnawardIcon}
            warning={true}
          >
            Unaward All
          </Button>
          <Button
            classes={{ iconHolder: classes.buttonIcon }}
            primary
            icon={AcceptOffersIcon}
            disabled={blockAction}
            loading={acceptingAll || blockAction}
            loadingText="Accepting Offers"
            onClick={!displayPostUpdatesAward ? handleAcceptAllClick : displayAwardedOfferModalHandler}
            cypressTag="accept-offers-button"
          >
            Accept Offers
          </Button>
        </div>
        <div className={classes.bannerContainer}>
          {displayPostUpdatesAward && <UpdatedAwardedOffers numberOfOffers={0} onClick={displayAwardedOfferModalHandler} />}
        </div>
        <div className={classes.table__container}>
          <DataTableNaked
            data={offerListingsByBuyerData?.offerListingsByBuyerQuery || []}
            groupBy={(row) => row.sellerSiteName}
            searchText={searchText}
            onRowClick={handleRowClick}
            columns={getColumnsFromDataTableProfile('Offer Review Buyer', user.site.dataTableProfiles, columnOverrides).filter(
              (column) => truckMaximumsSet || column.field !== 'truckCount',
            )}
            sticky
            rowActions={rowActions}
            headerRowActions={headerRowActions}
            isHeaderRowClickable={false}
            cypressTagTable="offer-revfgeneriew-buyer-table"
          />
        </div>
        <PostAwardUpdatesModal
          displayAwardedOffersModal={displayAwardedOffersModal}
          navigateToOfferCompScreen={navigateToOfferCompScreen}
          cancel={closeAwardedOffersModal}
          modalInnerText={modalInnerText}
        />
      </div>
    </div>
  );
};

OfferLogisticsReview.propTypes = {
  match: PropTypes.object,
  user: PropTypes.object,
  history: PropTypes.object,
};

const ConnectedComponent = inject((store) => store)(observer(OfferLogisticsReview));

export default ConnectedComponent;

// Notes:
// 1.  Note: below we call sellerSiteId by sellerLocationId, because in graphql these are different.  On the backend,
//     'sellerSiteId' is always a parent site's id, while 'sellerLocationId' is a specific seller location id.  Here
//     on the front-end, sellerSiteId does not necessarily refer to the parent site, but instead the specific seller location
